/**
 * The old Meta ad addresses, sent on to where the ads live now — slice 65.
 *
 * From slice 42 to slice 64 Wend's ad drafts sat under `/campaigns/ads`: the
 * composer at `/campaigns/ads/new`, a draft at `/campaigns/ads/:draftId`, its
 * correction at `/campaigns/ads/:draftId/edit`. Slice 65 split the old Reach
 * into Reach (paid ads on the workspace's own Meta ad account) and Connect
 * (email and WhatsApp), and the ads moved with Reach to `/reach/ads…`. The old
 * addresses were real for twenty-two slices — in bookmarks, in browser
 * history, pasted into chats — so each one keeps working and lands on the same
 * thing at its new address.
 *
 * **Why a component and not `<Navigate to="/reach/ads/:draftId" />`.**
 * `Navigate` takes its `to` literally: it does not fill a pattern from the
 * route that matched, so a bare `Navigate` on `/campaigns/ads/:draftId` would
 * send every draft to a page about a draft whose id is the text ":draftId". It
 * also drops the query string unless told otherwise, and a composer link can
 * carry one. {@link MovedAdRoute} fills the new pattern from the params that
 * matched and keeps `?…` and `#…` as they came, so the one thing that changes
 * is the path.
 *
 * **Replaced, not pushed.** Back from the new address must not land on the old
 * one, which would only redirect forward again — the trap where Back appears
 * to do nothing.
 *
 * The rejected alternative was to leave the old routes mounting the old pages.
 * It would have worked, and it would have left two addresses for every draft,
 * with the sidebar highlighting Connect's Campaigns over a Reach screen at one
 * of them — the "two answers" this split exists to remove.
 *
 * The path is built by {@link movedAdPath}, a pure function, so the suites can
 * prove the filling and the query without a browser to follow a redirect in.
 * The old routes stay declared in `App.tsx` **before** `/campaigns/:campaignId`
 * (React Router ranks a static segment above a param in any case; the order is
 * for the reader), so `ads` is never read as a campaign id.
 */
import { Navigate, useLocation, useParams } from 'react-router-dom';

/**
 * `pattern` with each `:name` replaced by that param, encoded, and the old
 * query and fragment carried across untouched.
 *
 * A param the route did not supply is left as an empty segment rather than
 * the literal `:name` — impossible for the routes this is mounted on, and if a
 * later edit made it possible, a 404 is the honest answer where a page about
 * a draft called ":draftId" is not.
 */
export function movedAdPath(
  pattern: string,
  params: Readonly<Record<string, string | undefined>>,
  search: string,
  hash: string,
): string {
  const path = pattern.replace(/:([A-Za-z0-9]+)/g, (_, name: string) => encodeURIComponent(params[name] ?? ''));
  return `${path}${search}${hash}`;
}

export interface MovedAdRouteProps {
  /** Where the ads live now, as a route pattern — `/reach/ads/:draftId/edit`. */
  readonly to: string;
}

export function MovedAdRoute({ to }: MovedAdRouteProps) {
  const params = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={movedAdPath(to, params, search, hash)} replace />;
}
